/* Copyright (c) 2021 Caring Transitions Online Auctions. All Rights Reserved. */

export class Validators {

    /** 
     * validates form controls on each input and verify form validity
     * 
     * @param formControls
     * 
     * @param inputEvent
     * 
     * @returns object containing formControl with new validity and form's validation status
     * 
     **/
    static formValidation = (controls, event) => {
        let controlError = '', formValid = true;

        /* checks all the validators for the control */
        for (let validator of controls[event.target.name]['validators']) {
            controlError = validator(event.target.value);
            if (controlError) {
                break;
            }
        }

        /* sets the state of the control and form validity */
        const newControls = Object.assign(controls, {
            [event.target.name]: {
                value: event.target.value,
                error: controlError,
                valid: !Boolean(controlError),
                validators: controls[event.target.name]['validators']
            }
        });

        /* check form validity */
        if (controlError) {
            formValid = false;
        } else {
            for (let control of Object.keys(newControls)) {
                if (!newControls[control].valid) {
                    formValid = false;
                    break;
                }
            }
        }

        return {
            formControls: newControls,
            formValid: formValid
        };
    };

    /**
     * check whether the form is valid or invalid 
     * 
     * @param formControls
     * 
     * @returns true if form is valid else false
     * 
     **/
    static checkFormValid = (formControls) => {
        const controls = Object.keys(formControls);
        for (let control of controls) {
            if (!formControls[control]['valid']) {
                return false;
            }
        }

        return true;
    };

    /**
     * checks whether value is empty or not
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static required = (msg = 'Please enter value') => {
        return (value) => {
            if ((Array.isArray(value) && value.length == 0) || !value) {
                return msg;
            }
            return '';
        };
    };

    /**
     * checks whether value is valid email or not
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static email = (msg = 'Please enter valid email address') => {
        return (value) => {
            const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (regex.test(value)) return '';
    
            return msg;
        };
    };

    /**
     * checks whether value is valid phone number or not
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static mobile = (msg = 'Please enter valid phone number') => {
        return (value) => {
            const mobileRegExp = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
            if (mobileRegExp.test(value)) return '';
    
            return msg;
        };
    };

    /**
     * checks whether value reached max characters or not
     * 
     * @param max maximum characters input value should have
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static maxLength = (max, msg = `Maximum ${max} characters`) => {
        return (value) => {
            if (value.length <= max) return '';

            return msg;
        };
    };

    /**
     * checks whether value reached max digit or not
     * 
     * @param max maximum digit input value should have
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static maxDigit = (max, msg = `Maximum ${max} digits`) => {
        return (value) => {
            if (Math.ceil(Math.log(parseInt(value) + 1) / Math.LN10) <= max || isNaN(parseInt(value))) return '';

            return msg;
        };
    };

    /**
     * checks whether value reached min digits or not
     * 
     * @param min minimum digit input value should have
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static minDigit = (min, msg = `Minimum ${min} digits`) => {
        return (value) => {
            if (Math.ceil(Math.log(parseInt(value) + 1) / Math.LN10) >= min) return '';

            return msg;
        };
    };

    /* character length should be equal to provided length */
    /**
     * checks whether value equals to characters or not
     * 
     * @param length input value characters should equals to
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static equalToLength = (length, msg = `Zip Code must be ${length} digits`) => {
        return (value) => {
            if (value.length === length) return '';

            return msg;
        };
    };

    /**
     * checks whether value is valid website or not
     * 
     * @param msg (Optional) pass a String value to display as error message
     * 
     * @returns empty string if valid else error message
     * 
     **/
    static websiteUrl = (msg = 'Invalid Website Address') => {
        return (value) => {
            const regex = new RegExp('^(https?:\\/\\/)?'+ // protocol
                '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
                '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
                '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
                '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
                '(\\#[-a-z\\d_]*)?$','i'); // fragment locator
            
            if (!value || !!regex.test(value)) {
                return '';
            } else {
                return msg;
            }
        };
    };

     /**
     * checks whether password valid or not
     * 
     * @param password password input value should have
     * 
     * @returns new password if valid else old password
     * 
     **/
    static checkPassword = (newPassword, oldPassword) => {
        if(newPassword){
            const passwordRegex = /^[a-zA-Z0-9!@#$%^&*]+$/g;
            if(passwordRegex.test(newPassword)){
                return newPassword;
            }
            else
                return oldPassword;
        }
        else
            return '';        
    };

}